import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Lock, RefreshCw, Unlock } from "lucide-react";
import { Toast } from "../../components/Toast";

type PaletteSwatch = {
  id: string;
  hex: string;
  locked: boolean;
};

const PALETTE_SIZE = 4;

const generateHexColor = () => {
  const bytes = new Uint8Array(3);
  crypto.getRandomValues(bytes);
  return (
    "#" +
    Array.from(bytes)
      .map((value) => value.toString(16).padStart(2, "0"))
      .join("")
  );
};

const createSwatch = (): PaletteSwatch => ({
  id: crypto.randomUUID(),
  hex: generateHexColor(),
  locked: false,
});

export function ColorPaletteGenerator() {
  const { t } = useTranslation();
  const [palette, setPalette] = useState<PaletteSwatch[]>(() =>
    Array.from({ length: PALETTE_SIZE }, createSwatch)
  );
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const regeneratePalette = () => {
    setPalette((prev) =>
      prev.map((swatch) =>
        swatch.locked ? swatch : { ...swatch, hex: generateHexColor() }
      )
    );
  };

  const toggleLock = (id: string) => {
    setPalette((prev) =>
      prev.map((swatch) =>
        swatch.id === id ? { ...swatch, locked: !swatch.locked } : swatch
      )
    );
  };

  const copyToClipboard = async (hex: string) => {
    try {
      await navigator.clipboard.writeText(hex);
      setToastMessage(t("paletteGenerator.copied"));
    } catch {
      setToastMessage(t("paletteGenerator.copyFailed"));
    }
  };

  return (
    <div className="palette-generator">
      <div className="palette-actions">
        <button
          type="button"
          className="palette-generate-btn"
          onClick={regeneratePalette}
        >
          <RefreshCw size={18} />
          {t("paletteGenerator.generate")}
        </button>
        <p className="palette-hint">{t("paletteGenerator.hint")}</p>
      </div>

      <div className="palette-grid">
        {palette.map((swatch) => (
          <div
            key={swatch.id}
            className={`palette-swatch ${swatch.locked ? "locked" : ""}`}
          >
            <div
              className="palette-color"
              style={{ backgroundColor: swatch.hex }}
            />
            <div className="palette-info">
              <div className="palette-hex">{swatch.hex.toUpperCase()}</div>
              <div className="palette-buttons">
                <button
                  type="button"
                  className="palette-btn"
                  onClick={() => copyToClipboard(swatch.hex)}
                  aria-label={t("paletteGenerator.copy")}
                >
                  <Copy size={16} />
                  {t("paletteGenerator.copy")}
                </button>
                <button
                  type="button"
                  className="palette-btn secondary"
                  onClick={() => toggleLock(swatch.id)}
                  aria-label={
                    swatch.locked
                      ? t("paletteGenerator.unlock")
                      : t("paletteGenerator.lock")
                  }
                >
                  {swatch.locked ? <Unlock size={16} /> : <Lock size={16} />}
                  {swatch.locked
                    ? t("paletteGenerator.unlock")
                    : t("paletteGenerator.lock")}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
